import * as PIXI from 'pixi.js';
import { assertObject, assertString } from '../../validators';
import type { Resolution } from '../../types';
import type { AspectRatio } from '../../aspectRatioConfig';
import { WatermarkPositions } from '../../enums';
import { ColorAttribute } from '../attributes';
import type { SerializableMixinSettings } from '../serializableMixin';
import { TypedSerializableMixin } from '../serializableMixin';
import type { TemplateWatermarkManager } from '../watermark/TemplateWatermarkSettings';
import type { WatermarkLayout } from '../watermark/WatermarkLayout';

type WatermarkBoundsDisplaySettings = {
    strokeColor?: number;
    referenceResolution: Resolution;
    aspectRatio: AspectRatio;
    watermark: TemplateWatermarkManager;
    referenceToOutputMultiplier?: number;
} & SerializableMixinSettings;

export class WatermarkBoundsDisplay extends TypedSerializableMixin(PIXI.Graphics) {
    override name = 'watermarkBoundsDisplay';
    @ColorAttribute()
    accessor strokeColor = 0xff8888;
    #strokeWidthInOutputResolution = 3;
    #referenceResolution: Resolution;
    #aspectRatio: AspectRatio;
    #watermark: TemplateWatermarkManager;
    #referenceToOutputMultiplier: number;

    constructor(settings: WatermarkBoundsDisplaySettings) {
        super(settings);

        assertObject(settings.referenceResolution);
        assertString(settings.aspectRatio);
        assertObject(settings.watermark);

        this.strokeColor = settings.strokeColor ?? this.strokeColor;
        this.#referenceResolution = settings.referenceResolution;
        this.#aspectRatio = settings.aspectRatio;
        this.#watermark = settings.watermark;
        this.#referenceToOutputMultiplier = settings.referenceToOutputMultiplier ?? 1;
        this._draw();
    }

    // TODO(LUM-661): redraw from a reaction on the watermark's position, available positions and layout.
    /**
     * Redraws the guides. The owning scene calls this on every seek.
     * @internal
     */
    _draw() {
        this.clear();

        const layout = this.#watermark.getLayout(this.#aspectRatio);

        const thinStroke = Math.max(
            1,
            Math.round(this.#strokeWidthInOutputResolution * this.#referenceToOutputMultiplier),
        );
        const thickStroke = Math.max(
            1,
            Math.round(
                (this.#strokeWidthInOutputResolution + 2) * this.#referenceToOutputMultiplier,
            ),
        );

        this.lineStyle(thinStroke, this.strokeColor, 1);
        this.#watermark.availablePositions.forEach((position) =>
            this.#drawWatermarkBoundsForPosition(position, layout, this.#referenceResolution),
        );

        this.lineStyle(thinStroke, this.strokeColor, 0.5);
        this.#watermark.availablePositions.forEach((position) =>
            this.#drawMarginGuidesForPosition(position, layout, this.#referenceResolution),
        );

        this.lineStyle(thickStroke, this.strokeColor, 1);
        this.#drawWatermarkBoundsForPosition(
            this.#watermark.position,
            layout,
            this.#referenceResolution,
        );
    }

    #drawWatermarkBoundsForPosition(
        position: WatermarkPositions,
        layout: WatermarkLayout,
        referenceResolution: Resolution,
    ) {
        let [x, y, w, h] = [0, 0, layout.maxWidth, layout.maxHeight];

        switch (position) {
            case WatermarkPositions.TopLeft:
                x = layout.marginLeft;
                y = layout.marginTop;
                break;
            case WatermarkPositions.TopCenter:
                x = referenceResolution.width / 2 - w / 2;
                y = layout.marginTop;
                break;
            case WatermarkPositions.TopRight:
                x = referenceResolution.width - w - layout.marginLeft;
                y = layout.marginTop;
                break;
            case WatermarkPositions.CenterLeft:
                x = layout.marginLeft;
                y = referenceResolution.height / 2 - h / 2;
                break;
            case WatermarkPositions.Center:
                x = referenceResolution.width / 2 - w / 2;
                y = referenceResolution.height / 2 - h / 2;
                break;
            case WatermarkPositions.CenterRight:
                x = referenceResolution.width - w - layout.marginRight;
                y = referenceResolution.height / 2 - h / 2;
                break;
            case WatermarkPositions.BottomRight:
                x = referenceResolution.width - w - layout.marginLeft;
                y = referenceResolution.height - h - layout.marginBottom;
                break;
            case WatermarkPositions.BottomLeft:
                x = layout.marginLeft;
                y = referenceResolution.height - h - layout.marginBottom;
                break;
            case WatermarkPositions.BottomCenter:
                x = referenceResolution.width / 2 - w / 2;
                y = referenceResolution.height - h - layout.marginBottom;
                break;
        }

        this.drawRect(x, y, w, h);
    }

    #drawMarginGuidesForPosition(
        position: WatermarkPositions,
        layout: WatermarkLayout,
        referenceResolution: Resolution,
    ) {
        const [width, height, marginLeft, marginRight, marginTop, marginBottom] = [
            layout.maxWidth,
            layout.maxHeight,
            layout.marginLeft,
            layout.marginRight,
            layout.marginTop,
            layout.marginBottom,
        ];
        const halfWidth = Math.floor(width / 2);
        const halfHeight = Math.floor(height / 2);

        switch (position) {
            case WatermarkPositions.TopLeft:
                this.moveTo(halfWidth + marginLeft, 0);
                this.lineTo(halfWidth + marginLeft, marginTop);
                this.moveTo(0, marginTop + halfHeight);
                this.lineTo(marginLeft, marginTop + halfHeight);
                break;
            case WatermarkPositions.TopCenter:
                this.moveTo(referenceResolution.width / 2, 0);
                this.lineTo(referenceResolution.width / 2, marginTop);
                break;
            case WatermarkPositions.TopRight:
                this.moveTo(referenceResolution.width - marginRight - halfWidth, 0);
                this.lineTo(referenceResolution.width - marginRight - halfWidth, marginTop);
                this.moveTo(referenceResolution.width - marginRight, marginTop + halfHeight);
                this.lineTo(referenceResolution.width, marginTop + halfHeight);
                break;
            case WatermarkPositions.CenterLeft:
                this.moveTo(0, referenceResolution.height / 2);
                this.lineTo(marginLeft, referenceResolution.height / 2);
                break;
            case WatermarkPositions.Center:
                // No margin guides for center position
                break;
            case WatermarkPositions.CenterRight:
                this.moveTo(
                    referenceResolution.width - marginRight,
                    referenceResolution.height / 2,
                );
                this.lineTo(referenceResolution.width, referenceResolution.height / 2);
                break;
            case WatermarkPositions.BottomRight:
                this.moveTo(
                    referenceResolution.width - marginRight - halfWidth,
                    referenceResolution.height - marginBottom,
                );
                this.lineTo(
                    referenceResolution.width - marginRight - halfWidth,
                    referenceResolution.height,
                );
                this.moveTo(
                    referenceResolution.width - marginRight,
                    referenceResolution.height - marginBottom - halfHeight,
                );
                this.lineTo(
                    referenceResolution.width,
                    referenceResolution.height - marginBottom - halfHeight,
                );
                break;
            case WatermarkPositions.BottomLeft:
                this.moveTo(halfWidth + marginLeft, referenceResolution.height - marginBottom);
                this.lineTo(halfWidth + marginLeft, referenceResolution.height);
                this.moveTo(0, referenceResolution.height - marginBottom - halfHeight);
                this.lineTo(marginLeft, referenceResolution.height - marginBottom - halfHeight);
                break;
            case WatermarkPositions.BottomCenter:
                this.moveTo(
                    referenceResolution.width / 2,
                    referenceResolution.height - marginBottom,
                );
                this.lineTo(referenceResolution.width / 2, referenceResolution.height);
                break;
        }
    }
}
